import { makeStyles, tokens } from '@fluentui/react-components';

// Align the detail and creation title bars with the judge-list filters.
const RAIL_HEADER_HEIGHT = '60px';

export const useStyles = makeStyles({
  // -- 3-panel layout -------------------------------------------------------
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    marginTop: '-1rem',
    marginRight: '-1rem',
    marginBottom: '-1rem',
    marginLeft: '-1rem',
  },

  panelsRow: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },

  // -- Left panel: judge list -----------------------------------------------
  listRail: {
    position: 'relative',
    display: 'flex',
    flexShrink: 0,
    minWidth: '240px',
    maxWidth: '600px',
    minHeight: 0,
  },

  listRailHandle: {
    position: 'absolute',
    zIndex: 1,
    top: 0,
    bottom: 0,
    right: '-4px',
    width: '8px',
    cursor: 'col-resize',
    touchAction: 'none',
    backgroundColor: 'transparent',
    ':focus-visible': {
      outline: `1px solid ${tokens.colorNeutralStroke1}`,
    },
  },

  leftPanel: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },

  judgeList: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: `${tokens.spacingVerticalXS} 0`,
  },

  judgeListItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFaster,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },

  judgeListItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    borderLeftColor: tokens.colorBrandBackground,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
  },

  judgeListItemNew: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandBackground,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },

  judgeListItemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },

  judgeListItemName: {
    minWidth: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  judgeListItemType: {
    flexShrink: 0,
  },

  judgeListItemMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },

  leftFooter: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  // -- Center panel ---------------------------------------------------------
  centerPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },

  centerEmpty: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
    padding: tokens.spacingHorizontalXXL,
  },

  centerEmptyIcon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground4,
    opacity: 0.5,
  },

  centerEmptyText: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    color: tokens.colorNeutralForeground3,
  },

  centerEmptySub: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground4,
    textAlign: 'center' as const,
    maxWidth: '360px',
  },

  editorHeader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: tokens.spacingVerticalXS,
    minHeight: RAIL_HEADER_HEIGHT,
    boxSizing: 'border-box',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  detailEditorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    minHeight: RAIL_HEADER_HEIGHT,
    boxSizing: 'border-box',
    paddingTop: '0',
    paddingRight: tokens.spacingHorizontalXL,
    paddingBottom: '0',
    paddingLeft: tokens.spacingHorizontalXL,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },

  editorTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    minWidth: '0',
    flex: '1',
  },

  editorActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: '0',
  },

  editorNameError: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },

  editorName: {
    fontSize: '20px',
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    color: tokens.colorNeutralForeground1,
    flex: 1,
    border: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    backgroundColor: 'transparent',
    padding: 0,
  },

  editorDescription: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },

  // -- Overview tab cards ---------------------------------------------------
  overviewTab: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  overviewCard: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    backgroundColor: tokens.colorNeutralBackground1,
  },

  overviewCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  overviewCardTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    paddingBottom: tokens.spacingVerticalS,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },

  overviewCardTitleHelp: {
    textTransform: 'none',
    letterSpacing: 'normal',
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },

  overviewCardFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  overviewCardBody: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase300,
    whiteSpace: 'pre-wrap',
  },

  readOnlyTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase500,
    color: tokens.colorNeutralForeground1,
    overflowWrap: 'anywhere',
  },

  readOnlyDescription: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap',
    marginBottom: tokens.spacingVerticalM,
    overflowWrap: 'anywhere',
  },

  readOnlyDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: tokens.spacingHorizontalL,
    margin: 0,
  },

  readOnlyLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },

  readOnlyValue: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground1,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },

  readOnlyPrompt: {
    minHeight: '320px',
    margin: 0,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: '14px',
    lineHeight: '1.7',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },

  readOnlyText: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase400,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    color: tokens.colorNeutralForeground1,
  },

  sourceCode: {
    margin: 0,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: tokens.fontSizeBase300,
    lineHeight: '1.6',
    whiteSpace: 'pre',
    overflow: 'auto',
    maxHeight: '70vh',
    '& .token-comment': { color: tokens.colorNeutralForeground3 },
    '& .token-keyword': { color: tokens.colorPalettePurpleForeground2 },
    '& .token-string': { color: tokens.colorPaletteGreenForeground1 },
    '& .token-number': { color: tokens.colorPaletteBlueForeground2 },
    '& .token-function': { color: tokens.colorPaletteBlueForeground2 },
    '& .token-class-name': { color: tokens.colorPaletteBlueForeground2 },
    '& .token-boolean': { color: tokens.colorPalettePurpleForeground2 },
  },

  sourceTabs: {
    maxWidth: '100%',
  },

  sourceLayout: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
    alignItems: 'flex-start',
  },

  sourceNavigation: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    width: '200px',
    maxHeight: '70vh',
    overflowY: 'auto',
    gap: tokens.spacingVerticalXXS,
    '& button': {
      justifyContent: 'flex-start',
      textAlign: 'left',
      overflowWrap: 'anywhere',
      height: 'auto',
    },
  },

  sourceContent: {
    flex: 1,
    minWidth: 0,
  },

  readOnlySourcePath: {
    marginTop: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflowWrap: 'anywhere',
  },

  overviewFieldRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    width: '100%',
  },

  overviewFieldColumn: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: '200px',
  },

  fullWidthControl: {
    width: '100%',
  },

  // Router link styled to match a Fluent inline link.
  detailLink: {
    color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'none',
    ':hover': {
      color: tokens.colorBrandForegroundLinkHover,
      textDecorationLine: 'underline',
    },
  },

  editorTabs: {
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  editorFirstTab: {
    paddingLeft: '0',
  },

  editorBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    gap: tokens.spacingVerticalM,
  },

  // Horizontal wrapper so the version-history rail can sit beside the body.
  editorBodyRow: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
    overflow: 'hidden',
  },

  editorFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },

  footerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },

  // -- Right panel ----------------------------------------------------------
  rightPanel: {
    display: 'flex',
    flexDirection: 'column',
    width: '480px',
    minWidth: '480px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },

  rightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  rightHeaderIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor: tokens.colorBrandBackground,
  },

  rightHeaderTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    color: tokens.colorNeutralForeground1,
    flex: '1',
  },

  rightSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
  },

  rightSectionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    color: tokens.colorNeutralForeground1,
  },

  fullWidthButton: {
    width: '100%',
  },

  rightFooter: {
    marginTop: 'auto',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  rightBody: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
  },

  // -- Creation: name input -------------------------------------------------
  createNameInput: {
    flex: '1',
    fontSize: '20px',
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    '& input': {
      lineHeight: tokens.lineHeightBase500,
    },
  },

  // -- Creation: monospace textarea -----------------------------------------
  textareaMonospace: {
    fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: '13px',
    lineHeight: '20px',
  },

  // -- Creation: results in center ------------------------------------------
  resultsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  resultsBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },

  variantTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: tokens.fontSizeBase200,
  },

  variantTh: {
    textAlign: 'left' as const,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
  },

  variantTd: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase200,
  },

  variantWinner: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },

  outputFile: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: tokens.fontSizeBase200,
  },

  outputFileName: {
    fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: tokens.fontSizeBase100,
  },

  outputFileDesc: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    marginLeft: 'auto',
  },

  loadingContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    minHeight: 0,
  },
});
